import React, { useState } from "react";
import { Pencil, CheckCircle2, Archive, Undo2, Lock, Eye } from "lucide-react";
import { StatusBadge, Button, toast, ConfirmDialog } from "@/components/ds";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, type KnowledgeEntry } from "@/lib/api";
import { STATUS_META, catLabel, fmtDate } from "./constants";

export function EntryRow({ entry, onChanged, onEdit }: {
  entry: KnowledgeEntry; onChanged: () => void; onEdit: (e: KnowledgeEntry) => void;
}) {
  const { role } = useAuth();
  const isOwner = role === "owner";
  const [busy, setBusy] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const meta = STATUS_META[entry.approval_status] ?? STATUS_META.draft;
  const expired = entry.expires_at ? new Date(entry.expires_at).getTime() < Date.now() : false;
  const customerFacing = entry.customer_visible && !entry.internal_only;
  const canApprove = customerFacing ? isOwner : true;

  const run = async (fn: () => Promise<unknown>, msg: string) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-start sm:justify-between" data-testid={`kb-entry-${entry.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate text-sm font-semibold text-slate-900">{entry.title}</p>
          <StatusBadge tone={meta.tone} data-testid={`kb-status-${entry.id}`}>{meta.label}</StatusBadge>
          {entry.internal_only
            ? <StatusBadge tone="slate"><Lock className="h-3 w-3" /> Internal</StatusBadge>
            : entry.customer_visible && <StatusBadge tone="blue"><Eye className="h-3 w-3" /> Customer</StatusBadge>}
          {expired && <StatusBadge tone="red">Expired</StatusBadge>}
        </div>
        <p className="mt-1 line-clamp-2 text-sm text-slate-500">{entry.content}</p>
        <p className="mt-1.5 text-xs text-slate-400">
          {catLabel(entry.category)}
          {entry.effective_at && ` · from ${fmtDate(entry.effective_at)}`}
          {entry.expires_at && ` · until ${fmtDate(entry.expires_at)}`}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button variant="ghost" size="sm" onClick={() => onEdit(entry)} disabled={busy} data-testid={`kb-edit-${entry.id}`}>
          <Pencil className="h-4 w-4" />
        </Button>
        {entry.approval_status !== "approved" && (
          <Button variant="ghost" size="sm" disabled={busy || !canApprove}
            onClick={() => run(() => api.kbApprove(entry.id), "Approved")} data-testid={`kb-approve-${entry.id}`}>
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          </Button>
        )}
        {entry.approval_status === "approved" && (
          <Button variant="ghost" size="sm" disabled={busy}
            onClick={() => run(() => api.kbDraft(entry.id), "Moved to draft")} data-testid={`kb-unapprove-${entry.id}`}>
            <Undo2 className="h-4 w-4 text-slate-500" />
          </Button>
        )}
        {entry.approval_status !== "archived" && (
          <Button variant="ghost" size="sm" disabled={busy}
            onClick={() => setConfirmArchive(true)} data-testid={`kb-archive-${entry.id}`}>
            <Archive className="h-4 w-4 text-slate-500" />
          </Button>
        )}
      </div>
      <ConfirmDialog
        open={confirmArchive} onOpenChange={setConfirmArchive}
        title="Archive this entry?" description="Archived entries are excluded from AI retrieval. You can restore it to draft later."
        confirmLabel="Archive" destructive
        onConfirm={() => run(() => api.kbArchive(entry.id), "Archived")}
        testid="kb-archive-confirm"
      />
    </div>
  );
}
